/**
 * Hubble — FreightSimple admin dashboard.
 *
 * ## Startup sequence
 *
 * 1. initAuth() — Initialize MSAL for Azure AD authentication
 * 2. startMockWorker() — Register MSW service worker (if mocking enabled)
 * 3. Dynamic import of App, FixtureSwitcher, and ComponentInspector when enabled
 * 4. Render the React tree
 *
 * If any step rejects, handleBootFailure() takes over: a stale chunk after a
 * deploy gets one reload, anything else is reported to Sentry (tagged with
 * the boot stage) and the loader is replaced by a message and a Reload button.
 *
 * initAuth() and startMockWorker() run in PARALLEL. MSW must be ready
 * before React renders, otherwise the first API calls won't be intercepted.
 * Auth must complete before React renders, otherwise MsalProvider has no instance.
 *
 * ## Provider stack
 *
 * MsalProvider (Azure auth — only if auth is enabled)
 *   → ErrorBoundary (catches render errors)
 *     → ApiConfigProvider (configures API base URL + auth for useFetch)
 *       → TooltipProvider (Radix tooltip delay)
 *         → FixtureSwitcher (Ctrl+Arrow to cycle fixture variants)
 *           → ComponentInspector (dev tool, only when enabled in the user menu)
 *             → App (router + page content)
 *         → Toaster (toast notifications)
 */

// Imported first so tracing sees every fetch/XHR and Sentry sees every error.
import "./Setup/observability";
import "./Setup/errorReporting";
import "mapbox-gl/dist/mapbox-gl.css";
import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ApiConfigProvider } from "@freightsimple/hubble-api/useApiConfiguration";
import { useCurrentUser } from "@freightsimple/hubble-api/useCurrentUser";
import {
  mark,
  startTimedSpan,
} from "@freightsimple/observability/observability";
import { Toaster } from "@freightsimple/ui-core/Feedback/Toaster";
import { CallProvider } from "@/App/Call/CallProvider";
import {
  ColdStartOverlay,
  useColdLoadEntries,
  AUTO_SHOW_BOOT_THRESHOLD_MS,
} from "@freightsimple/dev-tools/ColdStart/ColdStartOverlay";
import { ColdStartFriendlyNotice } from "@/App/Dev/ColdStartFriendlyNotice";
import { summarizeColdStart } from "@/App/Dev/coldStartSummary";
import { ErrorBoundary } from "@freightsimple/app-shell/ErrorBoundary";
import { ThemeProvider } from "@freightsimple/ui-core/Theme/ThemeProvider";
import { initAuth } from "@/App/Auth/initAuth";
import { configureComponentInspector } from "@/App/Dev/componentInspectorConfig";
import { isComponentInspectorEnabled } from "@freightsimple/dev-tools/Inspector/inspectorState";
import { startMockWorker } from "@/App/Mock/worker";
import { type BootStage, handleBootFailure } from "@/Setup/handleBootFailure";
import {
  listenForStaleChunks,
  noteRecoveredReload,
} from "@/Setup/staleChunkRecovery";
import { isEngineeringTeamMember } from "@/Shared/Data/admins";
import {
  environment,
  apiServerUrl,
  isAuthenticationDisabled,
} from "./environment";
import "./index.css";

configureComponentInspector();
listenForStaleChunks();

const bootSpan = startTimedSpan("boot.total", {
  auth_disabled: isAuthenticationDisabled(),
});
mark("boot.start");

// Initialize MSAL and MSW mock worker in parallel, then render
const initParallelSpan = startTimedSpan("boot.init_parallel");
let bootStage: BootStage = "init_parallel";
Promise.all([initAuth(), startMockWorker()])
  .then(([msalInstance]) => startApp(msalInstance))
  .catch((error: unknown) => {
    initParallelSpan.end();
    bootSpan.recordError(error);
    bootSpan.end({ "boot.failed_stage": bootStage });
    handleBootFailure(error, bootStage);
  });

async function startApp(
  msalInstance: PublicClientApplication | undefined,
): Promise<void> {
  initParallelSpan.end();
  bootStage = "dynamic_imports";
  // Dynamic imports — these are code-split so they don't block auth/MSW init.
  // App is the main router, ComponentInspector and FixtureSwitcher are dev tools.
  // The inspector loads only when someone turned it on (the toggle reloads the
  // page): off, it renders its children and nothing else, so every other boot
  // skips its chunk and its 2.7k-file source map.
  const dynamicImportsSpan = startTimedSpan("boot.dynamic_imports");
  const [{ App }, { FixtureSwitcher }, ComponentInspector] = await Promise.all([
    import("./App/App"),
    import("./App/Dev/FixtureSwitcher"),
    isComponentInspectorEnabled()
      ? import("./App/Dev/ComponentInspector").then((m) => m.ComponentInspector)
      : undefined,
  ]);
  dynamicImportsSpan.end();
  bootStage = "render";

  // Pass the real env to ApiConfigProvider so getLoginRequest picks the scope
  // matching whichever API we're hitting. Every Hubble Azure app registration
  // has localhost SPA redirect URIs pre-registered, so this works for local
  // override too. Matching scope ⇒ matching token `aud` ⇒ backend's Graph
  // on-behalf-of exchange (sales-email send) succeeds.
  const apiConfig = {
    environment,
    basePath: apiServerUrl(),
    authDisabled: isAuthenticationDisabled(),
  };

  // Hubble's audience includes ops, sales, and finance. The Cold Start overlay
  // is an engineering diagnostic (raw boot timeline), so engineers get it while
  // everyone else gets the friendly "Hubble felt slow — copy details" notice on
  // a slow boot, which still lets them report sluggish loads. The Developer
  // Settings toggle is already engineering-gated (ENG-4803); this mirrors that
  // audience split for the auto-show path. Auth-disabled local dev counts as
  // engineering — useCurrentUser returns the synthetic dev@freightsimple.com,
  // which isn't in Admins.json, but engineers running locally need the panel.
  function ColdStartOverlayGate_() {
    const user = useCurrentUser(isAuthenticationDisabled());
    const isEngineer =
      isAuthenticationDisabled() || isEngineeringTeamMember(user?.email);
    const [dismissed, setDismissed] = useState(false);
    // Engineers get the dev-tools panel, which polls the boot timeline itself.
    // Everyone else gets the friendly notice on a slow boot, so only poll here
    // for the non-engineering branch (avoids a redundant second poller).
    const entries = useColdLoadEntries(!isEngineer && !dismissed);

    // Automated browsers (Cypress) never want the boot-timeline overlay: it's
    // an engineering diagnostic, and because preview/e2e runs auth-disabled
    // every session counts as "engineer" (see isEngineer above), so it would
    // always render and cover the app — blocking Cypress clicks on dialogs
    // underneath it. Humans browsing a preview still get it.
    if ("Cypress" in window) return undefined;

    if (isEngineer) return <ColdStartOverlay />;
    if (dismissed) return undefined;

    const summary = summarizeColdStart(entries);
    const slowBoot =
      summary.bootTotalMs !== undefined &&
      summary.bootTotalMs > AUTO_SHOW_BOOT_THRESHOLD_MS;
    if (!slowBoot) return undefined;

    return (
      <ColdStartFriendlyNotice
        summary={summary}
        entries={entries}
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  function Root_() {
    const content = (
      <StrictMode>
        <ThemeProvider
          storageKey="hubble:theme"
          backgrounds={{ light: "#eef0f4", dark: "#111115" }}
        >
          <ErrorBoundary>
            <ApiConfigProvider options={apiConfig}>
              <CallProvider>
                <TooltipPrimitive.Provider delayDuration={0}>
                  <FixtureSwitcher>
                    {ComponentInspector ? (
                      <ComponentInspector>
                        <App />
                      </ComponentInspector>
                    ) : (
                      <App />
                    )}
                  </FixtureSwitcher>
                  <Toaster />
                  <ColdStartOverlayGate_ />
                </TooltipPrimitive.Provider>
              </CallProvider>
            </ApiConfigProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </StrictMode>
    );

    // Wrap in MsalProvider only if auth is enabled.
    // When VITE_DISABLE_AUTHENTICATION=true, msalInstance is undefined.
    if (msalInstance) {
      return <MsalProvider instance={msalInstance}>{content}</MsalProvider>;
    }

    return content;
  }

  createRoot(document.getElementById("root")!).render(<Root_ />);
  // First paint after render — the only place we can reasonably measure
  // wall-clock from navigation start to user-visible content.
  requestAnimationFrame(() => {
    mark("boot.first_render");
    bootSpan.end();
    noteRecoveredReload();
  });
}
